import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { Package, Globe, Code, DollarSign, Calendar, Building, Tag, Shield, Search, Check, ArrowUpRight } from 'lucide-react';

// Helper to format type/category names
const formatName = (name) => {
    if (!name) return '—';
    return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export default function AdminApps() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedApp, setSelectedApp] = useState(null);
    const [filter, setFilter] = useState('all'); // all, marketplace, internal
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, published, active, pending, suspended
    
    // Writeback events state
    const [eventCategories, setEventCategories] = useState([]);
    const [selectedWritebackEvents, setSelectedWritebackEvents] = useState([]);
    const [savingWritebackEvents, setSavingWritebackEvents] = useState(false);
    const [showWritebackSection, setShowWritebackSection] = useState(false);

    useEffect(() => {
        console.log('AdminApps component mounted');
        fetchApps();
    }, []);

    const fetchApps = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/admin/apps');
            if (Array.isArray(res.data)) {
                setApps(res.data);
            }
        } catch (err) {
            alert('Failed to load apps');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (appId) => {
        if (!window.confirm('Approve this app for the marketplace?')) return;
        try {
            await api.post(`/api/admin/apps/${appId}/approve`);
            alert('App approved and published to the marketplace!');
            await fetchApps();
        } catch (err) {
            console.error('Failed to approve:', err);
            alert('Failed to approve the app');
        }
    };

    const handleSuspend = async (appId) => {
        if (!window.confirm('Suspend this app? It will be removed from the marketplace.')) return;
        try {
            await api.post(`/api/admin/apps/${appId}/suspend`);
            alert('App suspended');
            await fetchApps();
        } catch (err) {
            console.error('Failed to suspend:', err);
            alert('Failed to suspend the app');
        }
    };

    const handleDelete = async (appId) => {
        if (!window.confirm('Permanently delete this app? This cannot be undone.')) return;
        try {
            await api.delete(`/api/admin/apps/${appId}/delete`);
            alert('App deleted');
            await fetchApps();
        } catch (err) {
            console.error('Failed to delete:', err);
            alert('Failed to delete the app');
        }
    };

    const fetchAppPurchases = async (app) => {
        if (app.type !== 'public') {
            // No purchases for non-public apps
            setSelectedApp(app);
            return;
        }

        try {
            const purchasesRes = await api.get(`/api/admin/apps/${app.id}/purchases`);
            if (purchasesRes.data && Array.isArray(purchasesRes.data)) {
                const purchaseData = {
                    purchases: purchasesRes.data,
                    total_sales: purchasesRes.data.filter(p => p.status === 'completed').length,
                    total_revenue: purchasesRes.data
                        .filter(p => p.status === 'completed')
                        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
                };
                setSelectedApp({ ...app, ...purchaseData });
            } else {
                setSelectedApp({ ...app, purchases: [], total_sales: 0, total_revenue: 0 });
            }
        } catch (err) {
            console.error('Failed to fetch purchases:', err);
            // Still show the app details even if purchases fail to load
            setSelectedApp({ ...app, purchases: [], total_sales: 0, total_revenue: 0 });
        }
    };

    const viewDetails = (app) => {
        // Fetch purchase data when opening the modal
        fetchAppPurchases(app);
        // Also fetch writeback events
        fetchAppWritebackEvents(app.id);
    };

    const closeDetails = () => {
        setSelectedApp(null);
        setShowWritebackSection(false);
        setSelectedWritebackEvents([]);
    };
    
    // Fetch all event categories
    const fetchEventCategories = async () => {
        try {
            const res = await api.get('/api/admin/event-types/all');
            setEventCategories(res.data.categories || []);
        } catch (err) {
            console.error('Failed to fetch event categories:', err);
        }
    };
    
    // Fetch writeback events for an app
    const fetchAppWritebackEvents = async (appId) => {
        try {
            const res = await api.get(`/api/admin/apps/${appId}/writeback-events`);
            setSelectedWritebackEvents(res.data.allowed_writeback_events || []);
        } catch (err) {
            console.error('Failed to fetch writeback events:', err);
            setSelectedWritebackEvents([]);
        }
    };
    
    // Toggle a single writeback event
    const toggleWritebackEvent = (eventName) => {
        setSelectedWritebackEvents(prev => {
            if (prev.includes(eventName)) {
                return prev.filter(e => e !== eventName);
            } else {
                return [...prev, eventName];
            }
        });
    };
    
    // Toggle all events in a category
    const toggleWritebackCategory = (categoryEvents) => {
        const allSelected = categoryEvents.every(e => selectedWritebackEvents.includes(e));
        if (allSelected) {
            setSelectedWritebackEvents(prev => prev.filter(e => !categoryEvents.includes(e)));
        } else {
            setSelectedWritebackEvents(prev => {
                const newSet = new Set([...prev, ...categoryEvents]);
                return Array.from(newSet);
            });
        }
    };
    
    // Select/deselect all writeback events
    const selectAllWritebackEvents = () => {
        // Hide patient.record.deleted event for writeback functionality
        const allEvents = eventCategories.flatMap(cat => cat.events).filter(e => e !== 'patient.record.deleted');
        setSelectedWritebackEvents(allEvents);
    };
    
    const deselectAllWritebackEvents = () => {
        setSelectedWritebackEvents([]);
    };
    
    // Save writeback events
    const saveWritebackEvents = async () => {
        if (!selectedApp) return;
        setSavingWritebackEvents(true);
        try {
            await api.post(`/api/admin/apps/${selectedApp.id}/writeback-events`, {
                allowed_writeback_events: selectedWritebackEvents
            });
            alert('Writeback event permissions updated successfully!');
        } catch (err) {
            console.error('Failed to save writeback events:', err);
            alert('Failed to update writeback event permissions');
        } finally {
            setSavingWritebackEvents(false);
        }
    };
    
    // Fetch event categories on mount
    useEffect(() => {
        fetchEventCategories();
    }, []);

    const filteredApps = apps
        .filter(app => {
            if (filter === 'marketplace') return app.type === 'public';
            if (filter === 'internal') return app.type !== 'public';
            return true;
        })
        .filter(app => {
            if (!searchTerm) return true;
            const q = searchTerm.toLowerCase();
            return (
                app.name?.toLowerCase().includes(q) ||
                app.user_email?.toLowerCase().includes(q)
            );
        })
        .filter(app => {
            if (statusFilter === 'all') return true;
            const isApproved = app.is_approved;
            const isPublic = app.type === 'public';
            if (statusFilter === 'published') return isApproved && isPublic;
            if (statusFilter === 'active')    return isApproved && !isPublic;
            if (statusFilter === 'pending')   return !isApproved;
            if (statusFilter === 'suspended') return app.status === 'suspended';
            return true;
        });

    const getAppTypeIcon = (type) => {
        if (type === 'public') return <Package className="w-4 h-4" />;
        return <Code className="w-4 h-4" />;
    };

    const getAppTypeColor = (type) => {
        if (type === 'public') return 'bg-purple-100 text-purple-800';
        return 'bg-blue-100 text-blue-800';
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-wrap items-start gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold flex-shrink-0">App Management</h1>

                {/* Type Filter Tabs */}
                <div className="flex flex-wrap gap-2 ml-auto">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-3 py-1.5 rounded-lg text-sm ${filter === 'all'
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                    >
                        All Apps ({apps.length})
                    </button>
                    <button
                        onClick={() => setFilter('marketplace')}
                        className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm ${filter === 'marketplace'
                            ? 'bg-purple-600 text-white'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                    >
                        <Package className="w-4 h-4" />
                        Marketplace ({apps.filter(a => a.type === 'public').length})
                    </button>
                    <button
                        onClick={() => setFilter('internal')}
                        className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm ${filter === 'internal'
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                    >
                        <Code className="w-4 h-4" />
                        Internal ({apps.filter(a => a.type !== 'public').length})
                    </button>
                </div>
            </div>

            {/* Search and Status Filter Row */}
            <div className="bg-white p-4 rounded-lg shadow flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by app name or developer email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white text-gray-700"
                >
                    <option value="all">All Statuses</option>
                    <option value="published">Published</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending Review</option>
                    <option value="suspended">Suspended</option>
                </select>
            </div>

            {filteredApps.length === 0 ? (
                <div className="bg-white p-8 rounded-lg shadow text-center">
                    <p className="text-gray-600">No apps found.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="p-3 text-left whitespace-nowrap">App Name</th>
                                <th className="p-3 text-left whitespace-nowrap">Type</th>
                                <th className="p-3 text-left whitespace-nowrap">Category</th>
                                <th className="p-3 text-left whitespace-nowrap">Pricing</th>
                                <th className="p-3 text-left whitespace-nowrap">Developer</th>
                                <th className="p-3 text-left whitespace-nowrap">Status</th>
                                <th className="p-3 text-right whitespace-nowrap">Actions</th>
                                <th className="p-3 text-left whitespace-nowrap">Sales</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredApps.map((app) => (
                                <tr key={app.id} className="hover:bg-gray-50">
                                    <td className="p-3">
                                        <div className="flex items-center gap-2">
                                            {app.icon_url ? (
                                                <img src={app.icon_url} alt="" className="w-8 h-8 rounded flex-shrink-0" />
                                            ) : (
                                                <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                                                    {getAppTypeIcon(app.type)}
                                                </div>
                                            )}
                                            <span className="font-medium">{app.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <span className={`px-2 py-1 rounded text-xs inline-flex items-center gap-1 whitespace-nowrap ${getAppTypeColor(app.type)}`}>
                                            {getAppTypeIcon(app.type)}
                                            {app.type === 'public' ? 'Marketplace' : formatName(app.type)}
                                        </span>
                                    </td>
                                    <td className="p-3 text-sm whitespace-nowrap">{formatName(app.category)}</td>
                                    <td className="p-3 text-sm whitespace-nowrap">
                                        {app.type === 'public' ? (
                                            app.pricing_model === 'free' ? (
                                                <span className="text-green-600 font-medium">Free</span>
                                            ) : (
                                                <span className="font-medium">
                                                    ${app.price} {app.price_period && `/ ${app.price_period}`}
                                                </span>
                                            )
                                        ) : (
                                            <span className="text-gray-500">N/A</span>
                                        )}
                                    </td>
                                    <td className="p-3 text-sm max-w-[160px] truncate">{app.user_email || '—'}</td>
                                    <td className="p-3">
                                        {app.is_approved ? (
                                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs whitespace-nowrap">
                                                {app.type === 'public' ? 'Published' : 'Active'}
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs whitespace-nowrap">
                                                Pending Review
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => viewDetails(app)} className="text-blue-600 hover:underline text-sm">View</button>
                                            {!app.is_approved && (
                                                <button onClick={() => handleApprove(app.id)} className="text-green-600 hover:underline text-sm">Approve</button>
                                            )}
                                            {app.is_approved && (
                                                <button onClick={() => handleSuspend(app.id)} className="text-yellow-600 hover:underline text-sm">Suspend</button>
                                            )}
                                            <button onClick={() => handleDelete(app.id)} className="text-red-600 hover:underline text-sm">Delete</button>
                                        </div>
                                    </td>
                                    <td className="p-3 text-sm">
                                        {app.type === 'public' ? (
                                            <button onClick={() => viewDetails(app)} className="text-blue-600 hover:underline text-sm whitespace-nowrap">View Details</button>
                                        ) : (
                                            <span className="text-gray-500">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Enhanced App Details Modal */}
            {selectedApp && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
                    <div className="bg-white w-full max-w-4xl p-6 rounded-lg shadow-xl relative max-h-[90vh] overflow-y-auto">
                        <button
                            onClick={closeDetails}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
                        >
                            &times;
                        </button>

                        {/* Header */}
                        <div className="flex items-start gap-4 mb-6">
                            {selectedApp.icon_url ? (
                                <img src={selectedApp.icon_url} alt="" className="w-16 h-16 rounded-lg" />
                            ) : (
                                <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                    {getAppTypeIcon(selectedApp.type)}
                                </div>
                            )}
                            <div className="flex-1">
                                <h2 className="text-2xl font-bold">{selectedApp.name}</h2>
                                <p className="text-gray-600">{selectedApp.tagline}</p>
                                <div className="flex items-center gap-4 mt-2">
                                    <span className={`px-3 py-1 rounded-full text-sm inline-flex items-center gap-1 ${getAppTypeColor(selectedApp.type)}`}>
                                        {getAppTypeIcon(selectedApp.type)}
                                        {selectedApp.type === 'public' ? 'Marketplace App' : `${formatName(selectedApp.type)} App`}
                                    </span>
                                    {selectedApp.is_approved ? (
                                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                                            {selectedApp.type === 'public' ? 'Published' : 'Active'}
                                        </span>
                                    ) : (
                                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                                            Pending Review
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-gray-700 mb-2">Basic Information</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                        <div>
                                            <strong className="text-sm text-gray-600">Developer:</strong>
                                            <p>{selectedApp.user_email || 'Unknown'}</p>
                                        </div>
                                        <div>
                                            <strong className="text-sm text-gray-600">Category:</strong>
                                            <p>{formatName(selectedApp.category) || 'Uncategorized'}</p>
                                        </div>
                                        <div>
                                            <strong className="text-sm text-gray-600">Created:</strong>
                                            <p>{new Date(selectedApp.created_at).toLocaleString()}</p>
                                        </div>
                                        {selectedApp.tags && (
                                            <div>
                                                <strong className="text-sm text-gray-600">Tags:</strong>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {selectedApp.tags.split(',').map((tag, idx) => (
                                                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                                                            {tag.trim()}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {selectedApp.type === 'public' && (
                                    <div>
                                        <h3 className="font-semibold text-gray-700 mb-2">Pricing Information</h3>
                                        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                            <div>
                                                <strong className="text-sm text-gray-600">Model:</strong>
                                                <p className="capitalize">{selectedApp.pricing_model || 'Free'}</p>
                                            </div>
                                            {selectedApp.pricing_model !== 'free' && (
                                                <>
                                                    <div>
                                                        <strong className="text-sm text-gray-600">Price:</strong>
                                                        <p className="text-lg font-semibold">
                                                            ${selectedApp.price || 0}
                                                            {selectedApp.price_period && ` / ${selectedApp.price_period}`}
                                                        </p>
                                                    </div>
                                                    {selectedApp.free_trial_days > 0 && (
                                                        <div>
                                                            <strong className="text-sm text-gray-600">Free Trial:</strong>
                                                            <p>{selectedApp.free_trial_days} days</p>
                                                        </div>
                                                    )}
                                                    {selectedApp.refund_policy && (
                                                        <div>
                                                            <strong className="text-sm text-gray-600">Refund Policy:</strong>
                                                            <p>{selectedApp.refund_policy}</p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {selectedApp.type === 'public' && selectedApp.purchases && selectedApp.purchases.length > 0 && (
                                    <div className="mt-6">
                                        <h3 className="font-semibold text-gray-700 mb-2">Purchase History ({selectedApp.purchases.length})</h3>
                                        <div className="bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto">
                                            <table className="w-full text-sm">
                                                <thead className="sticky top-0 bg-gray-100">
                                                    <tr>
                                                        <th className="p-2 text-left">Customer</th>
                                                        <th className="p-2 text-left">Amount</th>
                                                        <th className="p-2 text-left">Status</th>
                                                        <th className="p-2 text-left">Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y">
                                                    {selectedApp.purchases.map((purchase) => (
                                                        <tr key={purchase.id}>
                                                            <td className="p-2">
                                                                <div>
                                                                    <div className="font-medium">{purchase.user.name}</div>
                                                                    <div className="text-xs text-gray-500">{purchase.user.email}</div>
                                                                </div>
                                                            </td>
                                                            <td className="p-2">${purchase.amount.toFixed(2)}</td>
                                                            <td className="p-2">
                                                                <span className={`px-2 py-1 rounded text-xs ${purchase.status === 'completed'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-yellow-100 text-yellow-800'
                                                                    }`}>
                                                                    {purchase.status}
                                                                </span>
                                                            </td>
                                                            <td className="p-2 text-xs">
                                                                {new Date(purchase.created_at).toLocaleDateString()}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {selectedApp.company_name && (
                                    <div>
                                        <h3 className="font-semibold text-gray-700 mb-2">Company Information</h3>
                                        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                            <div>
                                                <strong className="text-sm text-gray-600">Company:</strong>
                                                <p>{selectedApp.company_name}</p>
                                            </div>
                                            {selectedApp.company_size && (
                                                <div>
                                                    <strong className="text-sm text-gray-600">Size:</strong>
                                                    <p>{selectedApp.company_size}</p>
                                                </div>
                                            )}
                                            {selectedApp.founded_year && (
                                                <div>
                                                    <strong className="text-sm text-gray-600">Founded:</strong>
                                                    <p>{selectedApp.founded_year}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right Column */}
                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-gray-700 mb-2">Description</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <p className="text-sm">
                                            {selectedApp.short_description || selectedApp.description || 'No description provided'}
                                        </p>
                                        {selectedApp.long_description && (
                                            <div className="mt-3 pt-3 border-t">
                                                <p className="text-sm text-gray-600">{selectedApp.long_description}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {selectedApp.key_features && selectedApp.key_features.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-gray-700 mb-2">Key Features</h3>
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <ul className="space-y-1">
                                                {selectedApp.key_features.map((feature, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-sm">
                                                        <span className="text-green-600 mt-0.5">•</span>
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <h3 className="font-semibold text-gray-700 mb-2">Links</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                        {selectedApp.website_url && (
                                            <a
                                                href={selectedApp.website_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex items-center gap-2 text-blue-600 hover:underline text-sm"
                                            >
                                                <Globe className="w-4 h-4" />
                                                Website
                                            </a>
                                        )}
                                        {selectedApp.demo_url && (
                                            <a
                                                href={selectedApp.demo_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex items-center gap-2 text-blue-600 hover:underline text-sm"
                                            >
                                                <Globe className="w-4 h-4" />
                                                Demo
                                            </a>
                                        )}
                                        {selectedApp.documentation_url && (
                                            <a
                                                href={selectedApp.documentation_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex items-center gap-2 text-blue-600 hover:underline text-sm"
                                            >
                                                <Globe className="w-4 h-4" />
                                                Documentation
                                            </a>
                                        )}
                                        {selectedApp.support_url && (
                                            <a
                                                href={selectedApp.support_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex items-center gap-2 text-blue-600 hover:underline text-sm"
                                            >
                                                <Globe className="w-4 h-4" />
                                                Support
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Writeback Events Section */}
                        <div className="mt-6 border-t pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <ArrowUpRight className="w-5 h-5 text-orange-600" />
                                        Writeback Event Permissions
                                    </h3>
                                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                                        {selectedWritebackEvents.length} selected
                                    </span>
                                </div>
                                <button
                                    onClick={() => setShowWritebackSection(!showWritebackSection)}
                                    className="text-blue-600 hover:underline text-sm"
                                >
                                    {showWritebackSection ? 'Hide' : 'Configure'}
                                </button>
                            </div>
                            
                            {showWritebackSection && (
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-600 mb-4">
                                        Select which event types this app can <strong>write back</strong> to the ecosystem.
                                        The app will only see these events in their Writeback configuration.
                                    </p>
                                    
                                    <div className="flex gap-2 mb-4">
                                        <button
                                            onClick={selectAllWritebackEvents}
                                            className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            onClick={deselectAllWritebackEvents}
                                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-3 max-h-64 overflow-y-auto">
                                        {eventCategories.map((category, idx) => {
                                            // Hide patient.record.deleted event for writeback functionality
                                            const categoryEvents = category.events.filter(e => e !== 'patient.record.deleted');
                                            if (categoryEvents.length === 0) return null;
                                            const selectedCount = categoryEvents.filter(e => selectedWritebackEvents.includes(e)).length;
                                            const allSelected = categoryEvents.length > 0 && selectedCount === categoryEvents.length;
                                            
                                            return (
                                                <div key={idx} className="bg-white border rounded-lg p-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <button
                                                            onClick={() => toggleWritebackCategory(categoryEvents)}
                                                            className="flex items-center gap-2 font-medium text-gray-800 hover:text-orange-600 transition"
                                                        >
                                                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                                                allSelected ? 'bg-orange-600 border-orange-600' : 'border-gray-300'
                                                            }`}>
                                                                {allSelected && <Check className="w-3 h-3 text-white" />}
                                                            </div>
                                                            {category.name}
                                                        </button>
                                                        <span className="text-xs text-gray-500">
                                                            {selectedCount}/{categoryEvents.length}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1 ml-6">
                                                        {categoryEvents.map(event => (
                                                            <button
                                                                key={event}
                                                                onClick={() => toggleWritebackEvent(event)}
                                                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition ${
                                                                    selectedWritebackEvents.includes(event)
                                                                        ? 'bg-orange-100 border border-orange-500 text-orange-700'
                                                                        : 'bg-gray-50 border border-gray-200 text-gray-600 hover:border-gray-300'
                                                                }`}
                                                            >
                                                                <div className={`w-3 h-3 rounded border flex items-center justify-center flex-shrink-0 ${
                                                                    selectedWritebackEvents.includes(event)
                                                                        ? 'bg-orange-600 border-orange-600'
                                                                        : 'border-gray-300'
                                                                }`}>
                                                                    {selectedWritebackEvents.includes(event) && (
                                                                        <Check className="w-2 h-2 text-white" />
                                                                    )}
                                                                </div>
                                                                <span className="truncate">{event}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    
                                    <button
                                        onClick={saveWritebackEvents}
                                        disabled={savingWritebackEvents}
                                        className="mt-4 w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                    >
                                        {savingWritebackEvents ? 'Saving...' : 'Save Writeback Permissions'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3 justify-end mt-6 pt-6 border-t">
                            {!selectedApp.is_approved && (
                                <button
                                    onClick={() => {
                                        handleApprove(selectedApp.id);
                                        closeDetails();
                                    }}
                                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                                >
                                    <Shield className="w-4 h-4" />
                                    {selectedApp.type === 'public' ? 'Approve for Marketplace' : 'Approve'}
                                </button>
                            )}
                            {selectedApp.is_approved && (
                                <button
                                    onClick={() => {
                                        handleSuspend(selectedApp.id);
                                        closeDetails();
                                    }}
                                    className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700"
                                >
                                    Suspend
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    handleDelete(selectedApp.id);
                                    closeDetails();
                                }}
                                className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                            <button
                                onClick={closeDetails}
                                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}